import { useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';



// import axios from 'axios';
import { UserContext } from '@/app/UserProvider';
import { FormLayout } from '@/components/common/FormLayout';
import { LoginForm } from '@/components/login/LoginForm';
import { getUserAuthToken, setUserAuthToken } from '@/services/authService';
import { PRODUCT_NAME } from '@/shared/constance';
import { getAuthTokenFromCookie, removeAuthTokenFromCookie } from '@/shared/lib/client';



import Indicator from './indicator.svg?react';


export const LoginSuccess = () => {
  const { t, i18n } = useTranslation();
  const [isAnimated, setIsAnimated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [searchParams] = useSearchParams();

  const { user, setUser, hasToken, isLoading } = useContext(UserContext);

  const isCli = searchParams.get('product') === 'cli';
  const ticket = searchParams.get('ticket');

  // 处理ticket绑定逻辑
  useEffect(() => {
    const handleTicketBinding = async () => {
      // 如果用户已登录且存在ticket参数
      if (user && ticket) {
        try {
          // 查询ticket是否已绑定
          const response = await getUserAuthToken(ticket);
          // 如果没有data或data为空，则绑定ticket
          if (!response.data) {
            await setUserAuthToken(ticket);
            console.log('Ticket bound successfully');
          }
        } catch (error) {
          console.error('Failed to handle ticket binding:', error);
        }
      }
    };

    handleTicketBinding();
  }, [user, ticket]);

  // Logout logic
  const handleLogout = async () => {
    // const token = getAuthTokenFromCookie();
    try {
      // Call backend logout API
      // await axios.get('/api/user/logout', {
      //   headers: { Authorization: `Bearer ${token}` },
      // });

      // Remove local cookie
      removeAuthTokenFromCookie();

      // Clear user state
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');

      toast.success(t('loginSuccess.logoutSuccessful'));

      // Refresh page to recheck login status
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if backend call fails, remove local cookie
      removeAuthTokenFromCookie();
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');

      toast.error(t('loginSuccess.logoutFailed'));

      // Refresh page to recheck login status
      window.location.reload();
    }
  };

  useEffect(() => {
    // Check user info loading status
    if (!isLoading && hasToken && !user) {
      // If there's a token but no user info and not loading, it means getting user info failed
      // Show login form after a delay
      const timer = setTimeout(() => {
        setShowLoginForm(true);
      }, 2000); // Show login form after 2 seconds

      return () => clearTimeout(timer);
    }
    if (user) {
      // Check if this is the first time showing login success
      const hasShownLoginSuccess = sessionStorage.getItem('hasShownLoginSuccess');

      if (!hasShownLoginSuccess) {
        // If there's user info and haven't shown success message yet, show it
        toast.success(t('loginSuccess.loginSuccessful'));
        // Mark that we've shown the login success message
        sessionStorage.setItem('hasShownLoginSuccess', 'true');
      }

      setTimeout(() => setIsAnimated(true), 100);
    }
  }, [user, isLoading, hasToken, t]);

  // Function to open Kwaipilot desktop app
  const handleOpenDesktopApp = () => {
    try {
      // Build complete app URL, can include login status or token
      const token = getAuthTokenFromCookie();
      const appUrl = `${PRODUCT_NAME.toLocaleLowerCase()}://login?token=${token}&source=web`;

      // eslint-disable-next-line no-console
      console.log('Starting codeflicker desktop app...', appUrl);

      // Try to open codeflicker:// protocol
      window.location.href = appUrl;
    } catch (error) {
      console.error('Error occurred while starting desktop app:', error);
      alert(t('loginSuccess.desktopAppError'));
    }
  };

  // If need to show login form (token exists but user info retrieval failed)
  if (showLoginForm) {
    return (
      <FormLayout title={t('auth.login.title')}>
        <LoginForm />
      </FormLayout>
    );
  }

  // If loading user info, show loading state
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[#F6F6F6] px-4">
        <div className="flex flex-col items-center gap-4">
          <div className="size-8 animate-spin rounded-full border-b-2 border-[#326BFB]" />
          <p className="text-[#575859]">{t('loginSuccess.verifyingStatus')}</p>
        </div>
      </div>
    );
  }

  // Show login success page
  return (
    <div className="flex flex-1 items-center justify-center bg-[#F6F6F6] px-4">
      <div
        className={`flex w-full max-w-[581px] flex-col items-center gap-9 transition-all duration-700 ${isAnimated ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
      >
        {/* User Status Indicator */}
        <Indicator />

        {/* Main Content */}
        <div className="flex w-full flex-col items-center gap-9">
          {/* Title and Description */}
          <div className="flex w-full flex-col items-center gap-3">
            <h1 className="font-urbanist text-center text-5xl font-bold leading-normal text-[#23343E]">
              {t('loginSuccess.title', { productName: PRODUCT_NAME })}
            </h1>
            <p className="font-urbanist text-center text-lg leading-5 text-[#575859]">
              {t('loginSuccess.description', { productName: PRODUCT_NAME })}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex w-full justify-center max-w-[391px] items-center gap-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex h-10 items-center justify-center gap-2.5 rounded border border-[#D5D5D5] bg-[#F5F7FA] px-[22px] py-2.5 transition-colors hover:bg-[#E8EBF0]"
            >
              <span className="font-adelphi whitespace-nowrap text-base font-medium text-black">
                {t('loginSuccess.logoutButton')}
              </span>
            </button>
            {!isCli && (
              <button
                type="button"
                onClick={handleOpenDesktopApp}
                className="flex h-10 flex-1 items-center justify-center gap-2.5 rounded bg-[#326BFB] px-4 py-2.5 transition-colors hover:bg-[#2557E8]"
              >
                <span className="font-adelphi whitespace-nowrap text-base font-medium text-white">
                  {t('loginSuccess.openAppButton')}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
